import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAccount, useGetNetworkConfig, useGetPendingTransactions, useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks';
import BigNumber from 'bignumber.js';
import egld_white_logo from 'assets/img/EGLD_white.svg';
import { EPunkStepper } from 'components/Elements/stepper';
import { getNftsByCollectionFromApi, getNftsFromXoxno, getRarityFromXoxno } from 'z/api';
import { queryCommonSettings, queryComputeNftPrices, sendBorrowEgld, sendBuyNftWithDownpayment } from 'z/elrond';
import { CommonSettingsType, NftRarity, NftWithIndexType } from 'z/types';
import { convertBigNumberValueToLocalString, convertEsdtToWei, convertWeiToEsdt, EGLD_DECIMALS, ERROR_CONNECT_YOUR_WALLET, ERROR_DATA_NOT_LOADED, ERROR_EXCEED_MAX_VALUE, ERROR_INVALID_VALUE, ERROR_VALUE_CANNOT_BE_NEGATIVE, formatLocalDateTime, MAX_PERCENTAGE, parseBigNumber, SECOND_TO_MILLISECONDS, toastError } from 'z/utils';
import { EnterAmount } from './EnterAmount';
import { Review } from './Review';
import { SelectNft } from './SelectNft';
import { COLLECTIONS_MAP, PAGE_SIZE, XOXNO_API_URL, XOXNO_FILTER } from 'data';
import { routeNames } from 'routes';

const steps = ['Select NFT', 'Enter Borrow Amount', 'Review'];

export const Mortgage = () => {
  const navigate = useNavigate();
  const {
    network: { apiAddress },
    chainID,
  } = useGetNetworkConfig();
  const { address } = useGetAccount();
  const { hasPendingTransactions } = useGetPendingTransactions();

  const [activeStep, setActiveStep] = useState<number>(0);

  const [commonSettings, setCommonSettings] = useState<CommonSettingsType | undefined>();
  const [selectedNftCollectionIndex, setSelectedNftCollectionIndex] = useState<number>(0);
  const [nfts, setNfts] = useState<any[]>([]);
  const [visibleNfts, setVisibleNfts] = useState<any[]>([]);
  const [nftLoading, setNftsLoading] = useState<number>(0);
  const [selectedNftIndex, setSelectedNftIndex] = useState<number>(-1);
  const [nftPrices, setNftPrices] = useState<string[]>([]);
  const [nftPricesLoading, setNftPricesLoading] = useState<number>(0);

  const [searchKey, setSearchKey] = useState<string>('');

  const [borrowAmount, setBorrowAmount] = useState<string>('0');
  const [borrowAmountError, setBorrowAmountError] = useState('');
  const [selectedBorrowPlanIndex, setSelectedBorrowPlanIndex] = useState<number>(0);

  const [liquidationPrice, setLiquidationPrice] = useState<string>('0');
  const [repaymentDeadline, setRepaymentDeadline] = useState<string>('');
  const [debtAmount, setDebtAmount] = useState<string>('0');
  const [borrowAvailableAmount, setBorrowAvailableAmount] = useState<string>('0');
  const [pageCounts, setPageCounts] = useState<number>(1);
  const [page, setPage] = useState<number>(1);

  const [rarity, setRarity] = useState<NftRarity[]>([]);

  useEffect(() => {
    if (hasPendingTransactions) return;

    (async () => {
      const _commonSettings = await queryCommonSettings(apiAddress);
      console.log('_commonSettings', _commonSettings);
      setCommonSettings(_commonSettings);
    })();
  }, [hasPendingTransactions]);

  const getNfts = (skip: number) => {
    (async () => {
      if (commonSettings && selectedNftCollectionIndex >= 0 && commonSettings.collection_ids.length > selectedNftCollectionIndex) {
        const url = XOXNO_API_URL + btoa(XOXNO_FILTER.replace('%s', commonSettings.collection_ids[selectedNftCollectionIndex]).replace('%d', skip.toString()));

        if (url) {
          setNftsLoading(1);
          let _nfts = [];
          let totalCounts = 0;
          if (address) {
            const nftData = await getNftsFromXoxno(url, address);
            _nfts = nftData.items;
            totalCounts = nftData.count;
          } else {
            const nftData = await getNftsFromXoxno(url);
            _nfts = nftData.items;
            totalCounts = nftData.count;
          }
          setPageCounts(parseInt((totalCounts / PAGE_SIZE).toString()) + 1);
          console.log('totalCounts', totalCounts);
          console.log('_nfts', _nfts);
          setNfts(_nfts);

          setNftsLoading(0);

          if (_nfts.length > 0) {
            setNftPricesLoading(1);
            const _nftPrices = await queryComputeNftPrices(apiAddress, _nfts);

            console.log('_nftPrices', _nftPrices);
            setNftPrices(_nftPrices);

            setNftPricesLoading(0);
          } else {
            setNftPrices([]);
          }
        }
      }
    })();
  };

  useEffect(() => {
    (async () => {
      if (commonSettings && selectedNftCollectionIndex >= 0 && commonSettings.collection_ids.length > selectedNftCollectionIndex) {
        const _rarity: NftRarity[] = await getRarityFromXoxno(commonSettings.collection_ids[selectedNftCollectionIndex]);
        // console.log('_rarity: ', _rarity);
        setRarity(_rarity);

        setPage(1);
        setPageCounts(1);
        getNfts(0);
      }
    })();
  }, [selectedNftCollectionIndex, commonSettings, address]);

  const handlePage = (pageNumber: number) => {
    setPage(pageNumber);
    console.log(pageNumber);
    getNfts((pageNumber - 1) * PAGE_SIZE);
  };

  useEffect(() => {
    let _visibleNfts = [];
    if (searchKey) {
      for (const nft of nfts) {
        console.log(nft.identifier);
        if (nft.identifier.toLowerCase().includes(searchKey.toLowerCase())) {
          _visibleNfts.push(nft);
        }
      }
    } else {
      _visibleNfts = nfts;
    }
    // console.log(_visibleNfts);
    setVisibleNfts(_visibleNfts);
  }, [nfts, searchKey]);

  const onClickNextButton = () => {
    if (!address) {
      toastError(ERROR_CONNECT_YOUR_WALLET);
      return;
    }
    if (selectedNftIndex < 0) {
      toastError('You must select an NFT');
      return;
    }
    if (!commonSettings) {
      toastError(ERROR_DATA_NOT_LOADED);
      return;
    }
    if (activeStep === 1) {
      if (BigNumber(borrowAmount).comparedTo(0) <= 0 || convertEsdtToWei(borrowAmount).gt(borrowAvailableAmount)) {
        toastError(ERROR_INVALID_VALUE);
        return;
      }
    }

    // buy nft with downpayment
    if (activeStep === 2) {
      buyNftWithDownpayment();
      return;
    }

    setActiveStep(activeStep + 1);
  };

  const onClickPrevButton = () => {
    if (activeStep === 0) {
      return;
    }
    setActiveStep(activeStep - 1);
  };

  const handleSelectedNft = (index: number) => {
    setSelectedNftIndex(index);
    setActiveStep(activeStep + 1);
  };

  // const handleChangeBorrowAmount = (value: number) => {
  //   if (selectedNFT.price < value) {
  //     toastError('You can not borrow over max amount.', {
  //       position: 'top-right',
  //     });
  //     return;
  //   }

  //   setSelectedNFT({ ...selectedNFT, borrow: value });
  // };

  // const handleSelectNFT = (nftData: any) => {
  //   const t_selectedNFT = { ...nftData, borrow: nftData.price };
  //   setSelectedNFT(t_selectedNFT);
  //   console.log(t_selectedNFT);
  //   // setActiveStep(activeStep + 1);
  // };

  useEffect(() => {
    if (commonSettings && selectedNftCollectionIndex >= 0 && selectedNftCollectionIndex < commonSettings.collection_ids.length && selectedNftIndex >= 0 && selectedNftIndex < nfts.length && selectedNftIndex < nftPrices.length && selectedBorrowPlanIndex < commonSettings.borrow_plan_count) {
      const _liquidationPrice = BigNumber(nftPrices[selectedNftIndex]).multipliedBy(commonSettings.global_liquidation_rate).div(MAX_PERCENTAGE).toFixed();
      console.log('_liquidationPrice', _liquidationPrice);
      setLiquidationPrice(_liquidationPrice);

      const _repaymentDeadline = formatLocalDateTime(Date.now() + commonSettings.max_borrow_periods[selectedBorrowPlanIndex] * SECOND_TO_MILLISECONDS);
      console.log('_repaymentDeadline', _repaymentDeadline);
      setRepaymentDeadline(_repaymentDeadline);

      let _borrowAvailableAmount = BigNumber.min(
        BigNumber(nftPrices[selectedNftIndex]).multipliedBy(commonSettings.collection_collateral_rates[selectedNftCollectionIndex]).div(MAX_PERCENTAGE)
        , commonSettings.global_max_borrow_amount).toFixed();
      if (selectedNftIndex >= 0 && selectedNftIndex < nfts.length && nfts[selectedNftIndex].saleInfoNft.max_bid) {
        _borrowAvailableAmount = BigNumber.min(_borrowAvailableAmount, BigNumber(nfts[selectedNftIndex].saleInfoNft.max_bid).multipliedBy(commonSettings.collection_collateral_rates[selectedNftCollectionIndex]).div(MAX_PERCENTAGE)).toFixed();
      }
      console.log('_borrowAvailableAmount', _borrowAvailableAmount);
      setBorrowAvailableAmount(_borrowAvailableAmount);
    }
  }, [selectedNftIndex, nftPrices, selectedBorrowPlanIndex]);

  useEffect(() => {
    if (commonSettings && selectedNftCollectionIndex >= 0 && selectedNftCollectionIndex < commonSettings.collection_ids.length && selectedNftIndex >= 0 && selectedNftIndex < nfts.length && selectedNftIndex < nftPrices.length && selectedBorrowPlanIndex < commonSettings.borrow_plan_count) {
      const _debtAmount = convertEsdtToWei(borrowAmount).multipliedBy(MAX_PERCENTAGE + commonSettings.borrow_rates[selectedBorrowPlanIndex]).div(MAX_PERCENTAGE).toFixed();
      console.log('_debtAmount', _debtAmount);
      setDebtAmount(_debtAmount);
    }
  }, [selectedBorrowPlanIndex, borrowAmount]);

  const onChangeBorrowAmount = (value: string) => {
    const bValue = parseBigNumber(value);
    let error = '';
    if (bValue == undefined) {
      error = 'Invalid number';
    } else if (bValue.comparedTo(0) < 0) {
      error = ERROR_VALUE_CANNOT_BE_NEGATIVE;
    } else if (convertEsdtToWei(bValue).comparedTo(borrowAvailableAmount) > 0) {
      error = ERROR_EXCEED_MAX_VALUE;
    }

    setBorrowAmountError(error);
    setBorrowAmount(value);
  };

  const onMaxBorrowAmount = () => {
    onChangeBorrowAmount(convertWeiToEsdt(borrowAvailableAmount, undefined, EGLD_DECIMALS).toFixed());
  };

  const buyNftWithDownpayment = async () => {
    if (!address) {
      toastError(ERROR_CONNECT_YOUR_WALLET);
      return;
    }
    if (!(commonSettings && selectedNftCollectionIndex >= 0 && selectedNftCollectionIndex < commonSettings.collection_ids.length && selectedNftIndex >= 0 && selectedNftIndex < nfts.length && selectedNftIndex < nftPrices.length && selectedBorrowPlanIndex < commonSettings.borrow_plan_count)) {
      toastError(ERROR_DATA_NOT_LOADED);
      return;
    }
    if (BigNumber(borrowAmount).comparedTo(0) <= 0 || convertEsdtToWei(borrowAmount).gt(borrowAvailableAmount)) {
      toastError(ERROR_INVALID_VALUE);
      return;
    }
    if (borrowAmountError) {
      toastError(borrowAmountError);
      return;
    }

    const result = await sendBuyNftWithDownpayment(
      chainID,
      address,
      nfts[selectedNftIndex].collection,
      nfts[selectedNftIndex].nonce,
      nfts[selectedNftIndex].saleInfoNft.max_bid,
      nfts[selectedNftIndex].saleInfoNft.auction_id,
      selectedBorrowPlanIndex,
      BigNumber(nfts[selectedNftIndex].saleInfoNft.max_bid).minus(convertEsdtToWei(borrowAmount)).toFixed(),
      debtAmount
    );

    setSessionId(result.sessionId);
  };

  const [sessionId, setSessionId] = useState<string>('');
  const transactionStatus = useTrackTransactionStatus({
    transactionId: sessionId,
  });

  useEffect(() => {
    if (transactionStatus.isSuccessful) {
      console.log('buyNftWithDownpayment: success');
      navigate(routeNames.loans);
    }
  }, [sessionId, hasPendingTransactions]);

  // useEffect(() => {
  //   if (ongoingSessionId > 0 && ongoingSessionId in pendingTransactions) {
  //     const tx = pendingTransactions[ongoingSessionId].transactions[0];
  //     console.log({tx});

  //     if (tx) {
  //       (async () => {
  //         const borrowEgld: BorrowEgldType = {
  //           loan_owner: address,
  //           lend_nft_tx_hash: tx.hash,
  //           collection_id: nftCollections[selectedNftCollectionIndex].collection_id,
  //           nft_id: nftsWithPrice[selectedNftIndex].identifier,
  //           nft_nonce: nftsWithPrice[selectedNftIndex].nonce,
  //           borrow_amount: convertEsdtToWei(borrowAmount).toFixed(),
  //           borrow_plan_id: borrowPlans[selectedBorrowPlanIndex].borrow_plan_id,
  //         };
  //         console.log('borrowEgld', borrowEgld);
  //         const res = await borrowEgldFromBackend(borrowEgld);
  //         console.log('borrowEgldFromBackend res', res);
  //         if (res.status === ReturnResultStatusTypeEnum.SUCCESS) {
  //           toast.success('Borrow EGLD request was successful', {
  //             position: 'top-right',
  //             duration: 5000,
  //           });

  //           // after success, sleep 5 seconds and redirect to My Loans page
  //           await sleep(5000);
  //           navigate(routeNames.loans);
  //         } else {
  //           toastError(res.message, {
  //             position: 'top-right',
  //             duration: 5000,
  //           });
  //         }

  //         // const apiNetworkProvider = new ApiNetworkProvider(apiAddress);
  //         // const watcher = new TransactionWatcher(apiNetworkProvider);
  //         // const txx = await watcher.awaitCompleted({ getHash: () => ({ hex: () => tx.hash }) });
  //         // console.log('txx', txx);

  //         // go to the first step
  //         // setActiveStep(0);
  //       })();
  //     }

  //     // clear
  //     setOngoingSessionId(0);
  //   }
  // }, [pendingTransactions]);

  return (
    <>
      <div className='container' style={{ marginTop: '40px' }}>
        <div className='info-item'>
          <div className='d-flex justify-content-between align-items-center'>
            <div className='title'>
              <span>Mortgage</span>
              {/* <span>Use your NFT as collateral and borrow EGLD</span> */}
            </div>
            <div className='egld-logo' >
              <img src={egld_white_logo} />
            </div>
          </div>

          <div style={{ marginTop: '60px' }}>
            <EPunkStepper steps={steps} activeStep={activeStep} />
          </div>

          <div style={{ marginTop: '60px' }}>
            {activeStep === 0 && (<SelectNft
              commonSettings={commonSettings}
              selectedNftCollectionIndex={selectedNftCollectionIndex}
              setSelectedNftCollectionIndex={setSelectedNftCollectionIndex}
              visibleNfts={visibleNfts}
              nftLoading={nftLoading}
              selectedNftIndex={selectedNftIndex}
              setSelectedNftIndex={setSelectedNftIndex}
              handleSelectedNft={handleSelectedNft}
              nftPrices={nftPrices}
              nftPricesLoading={nftPricesLoading}
              searchKey={searchKey}
              setSearchkey={setSearchKey}
              pageCounts={pageCounts}
              page={page}
              handlePage={handlePage}
              rarity={rarity}
            />)}
            {
              commonSettings && selectedNftCollectionIndex >= 0 && selectedNftCollectionIndex < commonSettings.collection_ids.length && selectedNftIndex >= 0 && selectedNftIndex < nfts.length && selectedNftIndex < nftPrices.length && selectedBorrowPlanIndex < commonSettings.borrow_plan_count && (<>
                {activeStep === 1 && (<EnterAmount
                  commonSettings={commonSettings}
                  selectedNft={nfts[selectedNftIndex]}
                  selectedNftPrice={nfts[selectedNftIndex].saleInfoNft.max_bid}
                  borrowAmount={borrowAmount}
                  onChangeBorrowAmount={onChangeBorrowAmount}
                  onMaxBorrowAmount={onMaxBorrowAmount}
                  selectedBorrowPlanIndex={selectedBorrowPlanIndex}
                  setSelectedBorrowPlanIndex={setSelectedBorrowPlanIndex}
                  selectedNftCollectionIndex={selectedNftCollectionIndex}
                  liquidationPrice={liquidationPrice}
                  repaymentDeadline={repaymentDeadline}
                  loanAmount={debtAmount}
                  borrowAvailableAmount={borrowAvailableAmount}
                  borrowAmountError={borrowAmountError}
                />)}
                {activeStep === 2 && (<Review
                  commonSettings={commonSettings}
                  selectedNft={nfts[selectedNftIndex]}
                  selectedNftPrice={nfts[selectedNftIndex].saleInfoNft.max_bid}
                  borrowAmount={borrowAmount}
                  selectedBorrowPlanIndex={selectedBorrowPlanIndex}
                  selectedNftCollectionIndex={selectedNftCollectionIndex}
                  liquidationPrice={liquidationPrice}
                  repaymentDeadline={repaymentDeadline}
                  loanAmount={debtAmount}
                  borrowAvailableAmount={borrowAvailableAmount}
                />)}
              </>)
            }
          </div>

          <div className='d-flex justify-content-end gap-3' style={{ marginTop: '30px' }}>
            {
              activeStep > 0 && activeStep < 3 &&
              (<div className="eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer' }} onClick={onClickPrevButton}>
                {'< Prev'}
              </div>)
            }
            {activeStep > 0 && (
              <button className="step-button eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer', textTransform: 'none' }} onClick={onClickNextButton}>
                {
                  activeStep < 2 ? 'Next >' : `Buy for ${convertBigNumberValueToLocalString(convertWeiToEsdt(BigNumber(nfts[selectedNftIndex].saleInfoNft.max_bid).minus(convertEsdtToWei(borrowAmount))))} EGLD`
                }
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
